import { useEffect } from 'react';
import { ArrowRight } from '@carbon/icons-react';
import {
  Link,
  useNavigate,
  useRouteLoaderData,
  isRouteErrorResponse,
} from 'react-router';
import { CarbonHeader } from './carbon-header.tsx';
import { type loader as rootLoader } from '../root.tsx';
import { NotifyProvider } from './notify.tsx';
import { useAnimatedNotification } from './animated-notification.tsx';

export function ServerError({ error }: { error?: unknown }) {
  const navigate = useNavigate();
  const rootData = useRouteLoaderData<typeof rootLoader>('root');
  const userPreference = rootData?.requestInfo?.userPrefs?.theme;

  const { showError, showSuccess, showInfo, showWarning } =
    useAnimatedNotification();

  // Check if we're in development mode
  const isDevelopment =
    typeof window !== 'undefined' && window.ENV?.MODE === 'development';

  // Set document title
  useEffect(() => {
    document.title = '500 - Internal Server Error | Bahandi';
  }, []);

  // Extract error details
  const errorMessage = error
    ? isRouteErrorResponse(error)
      ? `${error.status} ${error.statusText}: ${error.data}`
      : error instanceof Error
        ? error.message
        : String(error)
    : 'An unexpected error occurred';

  const errorStack = error instanceof Error ? error.stack : undefined;

  return (
    <NotifyProvider
      showSuccess={showSuccess}
      showError={showError}
      showInfo={showInfo}
      showWarning={showWarning}
    >
      <CarbonHeader
        userPreference={userPreference}
        showCalendarButton={true}
        showBellButton={true}
      />
      {rootData?.isImpersonating && (
        <div className="fixed top-12 left-0 right-0 z-9999 bg-yellow-500 text-black px-4 py-2 text-center font-semibold flex items-center justify-center gap-4">
          <span>⚠️ You are currently viewing as {rootData.user?.nickname}</span>
          <Link
            to="/api/exit-impersonation"
            className="bg-black text-yellow-500 px-3 py-1 rounded text-sm font-bold hover:bg-gray-800 no-underline"
          >
            Exit Impersonation
          </Link>
        </div>
      )}
      <div className="min-h-screen bg-background flex items-center justify-center p-6 selection:bg-primary-base/20">
        {/* Bulletproof Flexbox Layout (Matches 404 & 403 exactly) */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-10 max-w-3xl animate-in fade-in duration-700">
          {/* Left: The Error Code */}
          <h1 className="text-7xl md:text-8xl font-black text-foreground tracking-tighter leading-none">
            500
          </h1>

          {/* Center: The Structural Divider (1px wide, 24 units high) */}
          <div className="hidden md:block w-px h-24 bg-border" />

          {/* Right: The Context & Actions */}
          <div className="flex flex-col items-center md:items-start text-center md:text-left w-full">
            <h2 className="text-2xl md:text-3xl font-extrabold text-foreground tracking-tight mb-3">
              Internal server error
            </h2>

            {/* The HR/Office Joke + Reassurance */}
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed max-w-md mb-8">
              Looks like our system spilled coffee on its keyboard. We're
              experiencing an unexpected technical issue, but our support team
              has been notified and is on it.
            </p>

            {/* Development mode error details */}
            {isDevelopment && (
              <div className="mb-8 w-full max-w-2xl">
                <details className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <summary className="cursor-pointer font-semibold text-red-900 dark:text-red-100 mb-2">
                    Development Error Details
                  </summary>
                  <div className="mt-3 space-y-3">
                    <div>
                      <div className="text-xs font-bold text-red-800 dark:text-red-200 mb-1">
                        Error Message:
                      </div>
                      <pre className="text-xs bg-red-100 dark:bg-red-900/30 p-3 rounded overflow-x-auto text-red-900 dark:text-red-100 whitespace-pre-wrap wrap-break-word">
                        {errorMessage}
                      </pre>
                    </div>
                    {errorStack && (
                      <div>
                        <div className="text-xs font-bold text-red-800 dark:text-red-200 mb-1">
                          Stack Trace:
                        </div>
                        <pre className="text-xs bg-red-100 dark:bg-red-900/30 p-3 rounded overflow-x-auto text-red-900 dark:text-red-100 whitespace-pre-wrap wrap-break-word font-mono">
                          {errorStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              </div>
            )}

            {/* High-End Typographic Actions */}
            <div className="flex items-center gap-6">
              <button
                onClick={() => window.location.reload()}
                className="text-sm font-bold text-primary-base hover:text-primary-hover transition-colors flex items-center gap-1.5 group cursor-pointer"
              >
                Refresh the page
                <ArrowRight
                  size={16}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </button>

              <div className="w-1 h-1 rounded-full bg-border" />

              <Link
                to="/me"
                className="text-sm font-bold text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              >
                Go to My Desk
              </Link>
            </div>
          </div>
        </div>
      </div>
    </NotifyProvider>
  );
}
